import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { turnstileSiteKey } from "@base/configs";
import { DEFAULT_LANGUAGE } from "@base/language/lang";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

const useTurnstile = () => {
  // A callback ref, not a ref object: a page whose widget moves to another element (each step of a
  // wizard renders its own) has to rebuild it there, and only state can re-run the effect.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const [token, setToken] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const { i18n } = useTranslation();

  const isConfigured = !!turnstileSiteKey && turnstileSiteKey !== "undefined";
  // Turnstile defaults to "auto", which follows the browser locale - so the challenge can come up in
  // a language the rest of the page is not in. "vi" and "en" are both valid Turnstile codes, so our
  // own language codes go straight through.
  const language = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    if (!isConfigured || !container) {
      return;
    }

    const renderWidget = () => {
      if (!window.turnstile || widgetIdRef.current) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: turnstileSiteKey,
        // Pinned, not "auto": auto follows the OS, which would drop a dark widget into a light form.
        theme: "light",
        size: "flexible",
        language,
        callback: (_token: string) => {
          setToken(_token);
          setHasError(false);
        },
        "error-callback": () => {
          setToken(null);
          setHasError(true);
        },
        "expired-callback": () => {
          setToken(null);
        },
      });
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    // Kept so the cleanup can drop the listener again: with `language` in the dependency list this
    // effect re-runs, and a listener per run would stack up render calls on the same container.
    let pendingScript: HTMLScriptElement | null = null;

    if (window.turnstile) {
      renderWidget();
    } else if (existingScript) {
      pendingScript = existingScript;
      existingScript.addEventListener("load", renderWidget);
    } else {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", renderWidget);
      document.body.appendChild(script);
      pendingScript = script;
    }

    return () => {
      pendingScript?.removeEventListener("load", renderWidget);

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = undefined;
      }

      // Its token belonged to the widget just removed.
      setToken(null);
    };
    // `language` is baked into the widget at render time, so switching it means tearing the old
    // widget down and building a new one - it cannot be applied to a live widget.
  }, [isConfigured, language, container]);

  const resetWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setToken(null);
  }, []);

  return {
    containerRef,
    token,
    hasError,
    isConfigured,
    resetWidget,
  };
};

export default useTurnstile;
